import { Spacing } from '@persona-id/design-tokens';
import { CircleHelpIcon, CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { capitalize } from 'lodash';
import { Fragment, MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ChecksTable from 'dashboard/components/ChecksTable';
import Permissioned from 'dashboard/components/Permissioned';
import TabBarSwitch from 'dashboard/components/TabBarSwitch';
import TabSwitch from 'dashboard/components/TabBarSwitch/TabSwitch';
import Tooltip from 'dashboard/components/Tooltip';
import { VerificationDetailProps } from 'dashboard/components/VerificationDetail/types';
import LivePhoto from 'dashboard/components/VerificationDetail/VerificationTypes/GovernmentIdVerification/LivePhoto';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import { formatGridAndNonGridAttributes } from 'dashboard/lib/case-layouts/utils';
import { isSafari } from 'dashboard/lib/utils/device';
import {
  formatAddressFieldDisplay,
  formatDate,
  formatVehicleClass,
} from 'dashboard/lib/utils/formatters';
import {
  GovernmentIdPhotoType,
  NormalizedFile,
  PAGE_ORDER,
  PAGE_TO_LABEL,
  VerificationGovernmentId,
} from 'dashboard/models/verification';
import { GovernmentIdVerificationModuleAttribute } from 'dashboard/modules/ResourceVerification/model';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Banner from 'design-system/components/Banner';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import { COUNTRIES } from 'lib/constants/locales';
import { VERIFICATION_PAGE_DISPLAY } from 'lib/constants/verification';
import download from 'lib/download';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';
import { VerificationStatus } from 'lib/verification';

const StyledAttributeGrid = styled(AttributeGrid)`
  padding: 0 ${Spacing[4]} ${Spacing[2]};
`;

const ImageGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${Spacing[4]};
  padding-top: ${Spacing[2]};
`;

const InfoSection = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-row-gap: ${Spacing[4]};
`;

const PhotoLabel = styled.p`
  ${paragraphMixins.lgBold};
  margin: ${Spacing[2]} 0;
`;

const HorizontalDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const CircleHelpIconContainer = styled.div`
  padding: 0 ${Spacing[2]};
  display: flex;
  align-items: center;
`;

const VideoWrapper = styled.div`
  padding: 0 ${Spacing[3]};
  max-width: 400px;
  & video {
    width: 100%;
    border-radius: ${({ theme }) => theme.element.borderRadius};
  }
`;

const VideoErrorInner = styled.span`
  display: grid;
  grid-template-columns: auto auto;
  grid-gap: ${Spacing[2]};
  padding: ${Spacing[1]} 0;
  align-items: center;

  & button {
    background-color: transparent;
    color: ${({ theme }) => theme.color.text.brand};

    &:hover {
      cursor: pointer;
    }
  }
`;

interface ListItem {
  type: GovernmentIdPhotoType;
  content: string | ReactNode;
  index: number;
}

const LIVE_PHOTO_DESCRIPTION =
  'Live Photo is a composite animation created from multiple images captured from the Inquiry flow. This helps verify that the images contain a live individual, not a recording, picture, or another spoof.';

const GRID_ATTRIBUTES = [
  GovernmentIdVerificationModuleAttribute.Id,
  GovernmentIdVerificationModuleAttribute.FullName,
  GovernmentIdVerificationModuleAttribute.BirthDate,
  GovernmentIdVerificationModuleAttribute.Sex,
  GovernmentIdVerificationModuleAttribute.DocumentNumber,
  GovernmentIdVerificationModuleAttribute.IssuingCountry,
  GovernmentIdVerificationModuleAttribute.IssueDate,
  GovernmentIdVerificationModuleAttribute.ExpirationDate,
  GovernmentIdVerificationModuleAttribute.FromReusablePersona,
  GovernmentIdVerificationModuleAttribute.CaptureMethod,
  GovernmentIdVerificationModuleAttribute.CaptureMethodBack,
  GovernmentIdVerificationModuleAttribute.Address,
  GovernmentIdVerificationModuleAttribute.Nationality,
  GovernmentIdVerificationModuleAttribute.Remarks,
  GovernmentIdVerificationModuleAttribute.VisaStatus,
  GovernmentIdVerificationModuleAttribute.VehicleClasses,
];

interface GovernmentIdVerificationProps extends VerificationDetailProps {
  layout?: GovernmentIdVerificationModuleAttribute[];
  verification: VerificationGovernmentId;
}

const DEFAULT_LAYOUT = [
  GovernmentIdVerificationModuleAttribute.Photos,
  GovernmentIdVerificationModuleAttribute.Id,
  GovernmentIdVerificationModuleAttribute.FullName,
  GovernmentIdVerificationModuleAttribute.BirthDate,
  GovernmentIdVerificationModuleAttribute.Sex,
  GovernmentIdVerificationModuleAttribute.DocumentNumber,
  GovernmentIdVerificationModuleAttribute.IssuingCountry,
  GovernmentIdVerificationModuleAttribute.IssueDate,
  GovernmentIdVerificationModuleAttribute.ExpirationDate,
  GovernmentIdVerificationModuleAttribute.FromReusablePersona,
  GovernmentIdVerificationModuleAttribute.CaptureMethod,
  GovernmentIdVerificationModuleAttribute.CaptureMethodBack,
  GovernmentIdVerificationModuleAttribute.Address,
  GovernmentIdVerificationModuleAttribute.Nationality,
  GovernmentIdVerificationModuleAttribute.Remarks,
  GovernmentIdVerificationModuleAttribute.VisaStatus,
  GovernmentIdVerificationModuleAttribute.VehicleClasses,
  GovernmentIdVerificationModuleAttribute.Checks,
];

const GovernmentIdVerification = ({
  layout = DEFAULT_LAYOUT,
  verification,
}: GovernmentIdVerificationProps) => {
  const {
    'back-photo-url': backPhotoUrl,
    'front-photo-url': frontPhotoUrl,
    'photo-urls': displayPhotoUrls,
    'name-first': nameFirst,
    'name-middle': nameMiddle,
    'name-last': nameLast,
    'name-suffix': nameSuffix,
    birthdate,
    sex,
    'country-code': countryCode,
    'expiration-date': expirationDate,
    'issue-date': issueDate,
    'id-properties': idProperties,
    'identification-number': identificationNumber,
    'document-number': documentNumber,
    status,
    'from-persona': fromPersona,
    'from-reusable-persona': fromReusablePersona,
    'capture-method': captureMethod,
    'capture-method-back': captureMethodBack,
    'address-street-1': addressStreet1,
    'address-street-2': addressStreet2,
    'address-subdivision': addressSubdivision,
    'address-city': addressCity,
    'address-postal-code': addressPostalCode,
    nationality: nationality,
    remarks: remarks,
    'visa-status': visaStatus,
    'redacted-at': redactedAt,
    files: files,
    video,
  } = verification.attributes;
  const [currentTabIndex, setCurrentTabIndex] = useState(0);

  const preloadedImages: MutableRefObject<HTMLImageElement[]> = useRef([]);
  const preloadImages = (files: NormalizedFile[]) => {
    files.forEach((file) => {
      const image = new Image();
      image.src = file.url;
      preloadedImages.current.push(image);
    });
  };

  useEffect(() => {
    files
      ?.filter((file) => file['normalized-files'] && file['normalized-files'].length > 1)
      .forEach((file) => {
        preloadImages(file['normalized-files']);
      });
  }, []);

  const redacted = redactedAt != null;

  const showChecks =
    status != null &&
    ![
      VerificationStatus.Initiated,
      VerificationStatus.Confirmed,
      VerificationStatus.Submitted,
      VerificationStatus.Canceled,
    ].includes(status);

  const fullName = [nameFirst, nameMiddle, nameLast, nameSuffix].filter(Boolean).join(' ');

  const address = formatAddressFieldDisplay({
    addressStreet1: addressStreet1,
    addressStreet2: addressStreet2,
    addressCity: addressCity,
    addressSubdivision: addressSubdivision,
    addressPostalCode: addressPostalCode,
  });

  const vehicleClasses = idProperties?.['vehicle-classes'];

  const showNormalized = usePermission(Permission.DashboardVerificationsNormalizedView);

  const renderedNormalized =
    !redacted &&
    showNormalized &&
    files &&
    files
      .filter((file) => file['normalized-url'])
      .sort((a, b) => PAGE_ORDER[a.page] - PAGE_ORDER[b.page])
      .map((file) => {
        const label = PAGE_TO_LABEL[file.page];
        return (
          <Link
            to={`/dashboard/document-viewer/${verification?.relationships?.document?.data?.id}`}
            state={{ internalRedirect: true }}
            key={file.page}
          >
            <div data-test={`verification-detail__government-id-normalized-photos__${file.page}`}>
              <AsyncImage
                imageUrl={file['normalized-url']}
                width='initial'
                height='250px'
                modalLabel={label}
                allowModalExpand={true}
              />
              <PhotoLabel>{label}</PhotoLabel>
            </div>
          </Link>
        );
      });

  const renderedLive =
    !redacted &&
    showNormalized &&
    files &&
    files
      .filter((file) => file['normalized-files'] && file['normalized-files'].length > 1)
      .sort((a, b) => PAGE_ORDER[a.page] - PAGE_ORDER[b.page])
      .map((file) => {
        return (
          <Link
            to={`/dashboard/document-viewer/${verification?.relationships?.document?.data?.id}`}
            state={{ internalRedirect: true }}
            key={file.page}
          >
            <LivePhoto files={files} page={file.page} />
          </Link>
        );
      });

  const isVideoNotSupported = isSafari() && video?.['file-type'] === 'video/webm';

  const renderedOriginal = !redacted && (frontPhotoUrl || backPhotoUrl || displayPhotoUrls) && (
    <>
      {video && !isVideoNotSupported && (
        <VideoWrapper>
          <video controls>
            <source src={video.url} type={video['file-type']} />
          </video>
          <PhotoLabel>Video</PhotoLabel>
        </VideoWrapper>
      )}
      <ImageGridContainer data-test='verification-detail__government-id-original-photos'>
        {frontPhotoUrl && (
          <div>
            <Link
              to={`/dashboard/document-viewer/${verification?.relationships?.document?.data?.id}`}
              state={{ internalRedirect: true }}
            >
              <AsyncImage
                imageUrl={frontPhotoUrl}
                width='initial'
                height='250px'
                modalLabel='Front photo'
                allowModalExpand={true}
              />
            </Link>
            <PhotoLabel>Front photo</PhotoLabel>
          </div>
        )}
        {backPhotoUrl && (
          <div>
            <Link
              to={`/dashboard/document-viewer/${verification?.relationships?.document?.data?.id}`}
              state={{ internalRedirect: true }}
            >
              <AsyncImage
                imageUrl={backPhotoUrl}
                width='initial'
                height='250px'
                modalLabel='Back photo'
                allowModalExpand={true}
              />
            </Link>
            <PhotoLabel>Back photo</PhotoLabel>
          </div>
        )}
        {displayPhotoUrls &&
          displayPhotoUrls.map((displayPhotoUrl) => (
            <div key={displayPhotoUrl['url']}>
              <Link
                to={`/dashboard/document-viewer/${verification?.relationships?.document?.data?.id}`}
                state={{ internalRedirect: true }}
              >
                <AsyncImage
                  imageUrl={displayPhotoUrl['url']}
                  width='initial'
                  height='250px'
                  modalLabel={`${VERIFICATION_PAGE_DISPLAY[displayPhotoUrl['page']]} photo`}
                  allowModalExpand={true}
                />
              </Link>
              <PhotoLabel>{`${
                VERIFICATION_PAGE_DISPLAY[displayPhotoUrl['page']]
              } photo`}</PhotoLabel>
            </div>
          ))}
      </ImageGridContainer>
    </>
  );

  const tabOptions: ListItem[] = [];
  const tabs: ListItem[] = [];
  if (renderedOriginal) {
    tabs.push({
      content: renderedOriginal,
      type: GovernmentIdPhotoType.ORIGINAL,
      index: tabs.length,
    });
    tabOptions.push({
      content: 'Original',
      type: GovernmentIdPhotoType.ORIGINAL,
      index: tabOptions.length,
    });
  }
  if (renderedNormalized && renderedNormalized.length > 0) {
    tabs.push({
      content: (
        <ImageGridContainer data-test='verification-detail__government-id-normalized-photos-container'>
          {renderedNormalized}
        </ImageGridContainer>
      ),
      type: GovernmentIdPhotoType.NORMALIZED,
      index: tabs.length,
    });
    tabOptions.push({
      content: 'Normalized',
      type: GovernmentIdPhotoType.NORMALIZED,
      index: tabOptions.length,
    });
  }
  if (renderedLive && renderedLive.length > 0) {
    tabs.push({
      content: (
        <ImageGridContainer data-test='verification-detail__government-id-live-photos-container'>
          {renderedLive}
        </ImageGridContainer>
      ),
      type: GovernmentIdPhotoType.LIVE,
      index: tabs.length,
    });
    tabOptions.push({
      content: (
        <HorizontalDiv>
          <div>Live Photo</div>
          <Tooltip
            placement='top'
            trigger='hover'
            tooltip={
              <Paragraph
                size='sm'
                style={{
                  width: `400px`,
                }}
              >
                {LIVE_PHOTO_DESCRIPTION}
              </Paragraph>
            }
          >
            <CircleHelpIconContainer>
              <CircleHelpIcon width={12} />
            </CircleHelpIconContainer>
          </Tooltip>
        </HorizontalDiv>
      ),
      type: GovernmentIdPhotoType.LIVE,
      index: tabOptions.length,
    });
  }

  const getAttributeByType = (attr: GovernmentIdVerificationModuleAttribute) => {
    switch (attr) {
      case GovernmentIdVerificationModuleAttribute.Photos:
        if (tabs.length > 1) {
          return (
            <>
              <TabBarSwitch
                aria-label='Government ID Photos Navigation'
                data-test='verification-detail__government-id-tab-bar'
              >
                {tabOptions.map((tabOption) => (
                  <TabSwitch
                    key={tabOption.type}
                    id={`government-id-tab__${tabOption.type}`}
                    selected={currentTabIndex === tabOption.index}
                    onClick={() => {
                      setCurrentTabIndex(tabOption.index);
                    }}
                    data-test={`verification-detail__government-id-${tabOption.type}-photos-tab`}
                  >
                    {tabOption.content}
                  </TabSwitch>
                ))}
              </TabBarSwitch>
              {tabs.map((tab) => {
                return (
                  <Fragment key={tab.type}>{currentTabIndex === tab.index && tab.content}</Fragment>
                );
              })}
            </>
          );
        } else {
          return renderedOriginal;
        }
      case GovernmentIdVerificationModuleAttribute.Id:
        return (
          <Attribute
            label='ID number'
            value={
              <Permissioned
                permission={Permission.VerificationGovIdNumberView}
                fallback={
                  <Sticker
                    variant='basic'
                    data-test='verification-detail__government-id-id__not-available'
                  >
                    Not Available
                  </Sticker>
                }
              >
                {identificationNumber}
              </Permissioned>
            }
            redacted={redacted}
            data-test='verification-detail__government-id-id'
          />
        );
      case GovernmentIdVerificationModuleAttribute.FullName:
        return (
          <Attribute
            label='Full name'
            value={fullName}
            redacted={redacted}
            data-test='verification-detail__government-id-full-name'
          />
        );
      case GovernmentIdVerificationModuleAttribute.BirthDate:
        return (
          <Attribute
            label='Birthdate'
            value={formatDate(birthdate)}
            redacted={redacted}
            data-test='verification-detail__government-id-birthdate'
          />
        );
      case GovernmentIdVerificationModuleAttribute.DocumentNumber:
        return (
          <Attribute
            label='Document number'
            value={documentNumber}
            redacted={redacted}
            data-test='verification-detail__government-id-document-number'
          />
        );
      case GovernmentIdVerificationModuleAttribute.IssuingCountry:
        return (
          <Attribute
            label='Issuing country'
            value={countryCode ? COUNTRIES[countryCode] : null}
            redacted={redacted}
            data-test='verification-detail__government-id-issuing-country'
          />
        );
      case GovernmentIdVerificationModuleAttribute.IssueDate:
        return (
          <Attribute
            label='Issue date'
            value={formatDate(issueDate)}
            redacted={redacted}
            data-test='verification-detail__government-id-issue-date'
          />
        );
      case GovernmentIdVerificationModuleAttribute.ExpirationDate:
        return (
          <Attribute
            label='Expiration date'
            value={formatDate(expirationDate)}
            redacted={redacted}
            data-test='verification-detail__government-id-expiration-date'
          />
        );
      case GovernmentIdVerificationModuleAttribute.CaptureMethod:
        return (
          <Attribute
            label={
              fromPersona || fromReusablePersona
                ? 'Original capture method (front)'
                : 'Capture method (front)'
            }
            value={capitalize(captureMethod)}
            redacted={redacted}
            data-test='verification-detail__government-id-capture-method'
          />
        );
      case GovernmentIdVerificationModuleAttribute.CaptureMethodBack:
        return (
          <Attribute
            label={
              fromPersona || fromReusablePersona
                ? 'Original capture method (back)'
                : 'Capture method (back)'
            }
            value={capitalize(captureMethodBack)}
            redacted={redacted}
            data-test='verification-detail__government-id-capture-method-back'
          />
        );
      case GovernmentIdVerificationModuleAttribute.FromReusablePersona:
        return (
          <Attribute
            label='Reusable Persona used'
            value={fromPersona || fromReusablePersona ? 'Yes' : 'No'}
            redacted={redacted}
            data-test='verification-detail__government-id-reusable-persona-used'
          />
        );
      case GovernmentIdVerificationModuleAttribute.Address:
        return (
          <Attribute
            label='Address'
            value={address}
            redacted={redacted}
            data-test='verification-detail__government-id-address'
          />
        );
      case GovernmentIdVerificationModuleAttribute.Nationality:
        return (
          <Attribute
            label='Nationality'
            value={nationality ? COUNTRIES[nationality] : null}
            redacted={redacted}
            data-test='verification-detail__government-id-nationality'
          />
        );
      case GovernmentIdVerificationModuleAttribute.Remarks:
        return (
          remarks && (
            <Attribute
              label='Remarks'
              value={remarks}
              redacted={redacted}
              data-test='verification-detail__government-id-remarks'
            />
          )
        );
      case GovernmentIdVerificationModuleAttribute.Sex:
        return (
          <Attribute
            label='Sex'
            value={sex}
            redacted={redacted}
            data-test='verification-detail__government-id-sex'
          />
        );
      case GovernmentIdVerificationModuleAttribute.VisaStatus:
        return (
          visaStatus && (
            <Attribute
              label='Visa Status'
              value={visaStatus}
              redacted={redacted}
              data-test='verification-detail__government-id-visa-status'
            />
          )
        );
      case GovernmentIdVerificationModuleAttribute.VehicleClasses:
        return (
          vehicleClasses &&
          Object.entries(vehicleClasses).map(([key, value]) => (
            <Attribute
              key={key}
              label={`Vehicle class ${key}`}
              value={formatVehicleClass(value)}
              redacted={redacted}
              data-test='verification-detail__government-id-vehicle-classes'
            />
          ))
        );
      case GovernmentIdVerificationModuleAttribute.Checks:
        return (
          showChecks &&
          verification.attributes.checks && (
            <ChecksTable
              checks={verification.attributes.checks}
              additionalPhotos={{
                frontPhotoUrl: frontPhotoUrl,
                backPhotoUrl: backPhotoUrl,
              }}
            />
          )
        );
    }
  };

  return (
    <div data-test='verification-detail__government-id-verification'>
      <InfoSection>
        {isVideoNotSupported && (
          <Banner variant='warning'>
            <VideoErrorInner>
              <CircleInfoIcon />
              <span>
                Video playback not available in Safari. &nbsp;
                <button
                  id='government-id-verification__download-video'
                  onClick={() => {
                    download({ url: video.url, filename: video['file-name'] });
                  }}
                >
                  Download video
                </button>{' '}
                or view in another browser.
              </span>
            </VideoErrorInner>
          </Banner>
        )}
        {formatGridAndNonGridAttributes(
          layout,
          GRID_ATTRIBUTES,
          getAttributeByType,
          StyledAttributeGrid,
        )}
      </InfoSection>
    </div>
  );
};

export default GovernmentIdVerification;
